import { addDays, isDateOnly, parseDateOnly, toDateOnly } from "./dates";
import type { DueRule, Weekday } from "./types";

const WEEKDAY_INDEX: Record<Weekday, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function monthlyOccurrence(issueDate: string, dayOfMonth: number): string {
  const issue = parseDateOnly(issueDate);
  const safeDay = Math.min(31, Math.max(1, Math.round(dayOfMonth)));
  const year = issue.getFullYear();
  const month = issue.getMonth();
  const lastDayThisMonth = new Date(year, month + 1, 0, 12).getDate();
  let candidate = new Date(year, month, Math.min(safeDay, lastDayThisMonth), 12);
  if (candidate < issue) {
    const nextMonthLastDay = new Date(year, month + 2, 0, 12).getDate();
    candidate = new Date(year, month + 1, Math.min(safeDay, nextMonthLastDay), 12);
  }
  return toDateOnly(candidate);
}

export function calculateDueDate(issueDate: string, rule: DueRule): string | null {
  if (!isDateOnly(issueDate)) return null;
  switch (rule.kind) {
    case "none":
    case "custom":
      return null;
    case "upon_receipt":
      return issueDate;
    case "specific":
      return isDateOnly(rule.date) ? rule.date : null;
    case "net":
      return addDays(issueDate, Math.max(0, Math.round(rule.days)));
    case "weekly": {
      const issue = parseDateOnly(issueDate);
      const offset = (WEEKDAY_INDEX[rule.weekday] - issue.getDay() + 7) % 7;
      return addDays(issueDate, offset);
    }
    case "every_n_weeks": {
      if (!isDateOnly(rule.anchorDate)) return null;
      const intervalDays = Math.max(1, Math.round(rule.intervalWeeks)) * 7;
      const issue = parseDateOnly(issueDate).getTime();
      const anchor = parseDateOnly(rule.anchorDate).getTime();
      if (issue <= anchor) return rule.anchorDate;
      const elapsedDays = Math.floor((issue - anchor) / 86_400_000);
      const periods = Math.ceil(elapsedDays / intervalDays);
      return addDays(rule.anchorDate, periods * intervalDays);
    }
    case "monthly":
      return monthlyOccurrence(issueDate, rule.dayOfMonth);
    default: {
      const exhaustive: never = rule;
      return exhaustive;
    }
  }
}

export function dueRuleLabel(rule: DueRule): string {
  switch (rule.kind) {
    case "none":
      return "No due date";
    case "upon_receipt":
      return "Due upon receipt";
    case "specific":
      return "Specific due date";
    case "net":
      return `Net ${rule.days}`;
    case "weekly":
      return `Every ${rule.weekday.charAt(0).toUpperCase()}${rule.weekday.slice(1)}`;
    case "every_n_weeks":
      return `Every ${rule.intervalWeeks} week${rule.intervalWeeks === 1 ? "" : "s"} from ${rule.anchorDate}`;
    case "monthly":
      return `Monthly on day ${rule.dayOfMonth}`;
    case "custom":
      return rule.label.trim() || "Custom agreed terms";
    default: {
      const exhaustive: never = rule;
      return exhaustive;
    }
  }
}
